// =============================================================================
// AGC: DROID FARM - Stress Alerts Panel
// AGC: Displays list of stress alerts with severity indicators and actions
// =============================================================================

import React, { useState } from 'react'
import { 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  CheckCircle, 
  X, 
  ChevronDown, 
  ChevronUp,
  MapPin,
  Clock,
} from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import type { StressEvent, StressAlert, AlertSummary, StressEventStatus } from '../../../types'

interface StressAlertsPanelProps {
  alerts: StressAlert[]
  events: StressEvent[]
  summary: AlertSummary | null
  onEventStatusChange?: (eventId: string, status: StressEventStatus) => void
  onViewOnMap?: (event: StressEvent) => void
  isLoading?: boolean
}

/**
 * AGC: Get icon for severity level
 */
function getSeverityIcon(severity: string) {
  switch (severity) {
    case 'critical':
      return <AlertTriangle className="w-5 h-5 text-red-500" />
    case 'warning':
      return <AlertCircle className="w-5 h-5 text-amber-500" />
    case 'info':
    default:
      return <Info className="w-5 h-5 text-blue-500" />
  }
}

/**
 * AGC: Get badge color for severity
 */
function getSeverityBadgeClass(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'
    case 'warning':
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
    case 'info':
    default:
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
  }
}

/**
 * AGC: Individual alert item component
 */
const AlertItem: React.FC<{
  alert: StressAlert | StressEvent
  isEvent: boolean
  onStatusChange?: (eventId: string, status: StressEventStatus) => void
  onViewOnMap?: () => void
}> = ({ alert, isEvent, onStatusChange, onViewOnMap }) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  const title = 'title' in alert ? alert.title : alert.message
  const message = alert.message
  const severity = alert.severity
  const recommendation = 'recommendation' in alert ? alert.recommendation : undefined
  const actions = 'actions' in alert ? alert.actions : undefined
  const eventId = isEvent ? (alert as StressEvent).id : undefined
  const status = isEvent ? (alert as StressEvent).status : undefined

  return (
    <div className={`border rounded-lg overflow-hidden ${getSeverityBadgeClass(severity)}`}>
      {/* AGC: Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
      >
        {getSeverityIcon(severity)}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{title}</p>
          {!isExpanded && (
            <p className="text-xs opacity-75 truncate">{message}</p>
          )}
        </div>
        {isEvent && status === 'OPEN' && (
          <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
            {t('fields.monitoring.open')}
          </span>
        )}
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 flex-shrink-0" />
        )}
      </button>

      {/* AGC: Expanded Content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-current/10">
          {/* AGC: Message */}
          <p className="text-sm mt-2">{message}</p>

          {/* AGC: Recommendation */}
          {recommendation && (
            <div className="bg-white/50 dark:bg-black/20 rounded p-2">
              <p className="text-xs font-medium mb-1">{t('fields.monitoring.recommendation')}</p>
              <p className="text-xs">{recommendation}</p>
            </div>
          )}

          {/* AGC: Suggested Actions */}
          {actions && actions.length > 0 && (
            <div>
              <p className="text-xs font-medium mb-1">{t('fields.monitoring.suggestedActions')}</p>
              <ul className="text-xs space-y-1">
                {actions.map((action, index) => (
                  <li key={index} className="flex items-start gap-1.5">
                    <span className="text-current/60">•</span>
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* AGC: Actions */}
          {isEvent && eventId && onStatusChange && status === 'OPEN' && (
            <div className="flex items-center gap-2 pt-2 border-t border-current/10">
              {onViewOnMap && (
                <button
                  onClick={onViewOnMap}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-current/10 hover:bg-current/20 rounded transition-colors"
                >
                  <MapPin className="w-3 h-3" />
                  {t('fields.monitoring.viewOnMap')}
                </button>
              )}
              <button
                onClick={() => onStatusChange(eventId, 'RESOLVED')}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-green-500 hover:bg-green-600 text-white rounded transition-colors"
              >
                <CheckCircle className="w-3 h-3" />
                {t('fields.monitoring.markResolved')}
              </button>
              <button
                onClick={() => onStatusChange(eventId, 'DISMISSED')}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
              >
                <X className="w-3 h-3" />
                {t('fields.monitoring.dismiss')}
              </button>
            </div>
          )}

          {/* AGC: Event timestamp */}
          {isEvent && (alert as StressEvent).date && (
            <div className="flex items-center gap-1 text-xs opacity-60">
              <Clock className="w-3 h-3" />
              <span>{new Date((alert as StressEvent).date).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const StressAlertsPanel: React.FC<StressAlertsPanelProps> = ({
  alerts,
  events,
  summary,
  onEventStatusChange,
  onViewOnMap,
  isLoading = false,
}) => {
  const { t } = useTranslation()

  // AGC: Filter to open events only for main display
  const openEvents = events.filter(e => e.status === 'OPEN')
  
  // AGC: Combine alerts and events, sorted by severity
  const allItems = [
    ...alerts.map(a => ({ item: a, isEvent: false })),
    ...openEvents.map(e => ({ item: e as StressAlert | StressEvent, isEvent: true })),
  ].sort((a, b) => {
    const severityOrder = { critical: 0, warning: 1, info: 2 }
    return (severityOrder[a.item.severity as keyof typeof severityOrder] || 2) - 
           (severityOrder[b.item.severity as keyof typeof severityOrder] || 2)
  })

  // AGC: Loading state
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-16 bg-gray-100 dark:bg-gray-700 rounded" />
          <div className="h-16 bg-gray-100 dark:bg-gray-700 rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl p-4">
      {/* AGC: Header with Summary */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('fields.monitoring.stressAlerts')}
        </h4>
        
        {summary && (
          <div className="flex items-center gap-2">
            {summary.bySeverity.critical > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs rounded-full">
                <AlertTriangle className="w-3 h-3" />
                {summary.bySeverity.critical}
              </span>
            )}
            {summary.bySeverity.warning > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs rounded-full">
                <AlertCircle className="w-3 h-3" />
                {summary.bySeverity.warning}
              </span>
            )}
            {summary.bySeverity.info > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                <Info className="w-3 h-3" />
                {summary.bySeverity.info}
              </span>
            )}
          </div>
        )}
      </div>

      {/* AGC: No alerts state */}
      {allItems.length === 0 && (
        <div className="flex items-center justify-center p-6 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <div className="text-center">
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
            <p className="text-sm text-green-700 dark:text-green-300">
              {t('fields.monitoring.noAlerts')}
            </p>
          </div>
        </div>
      )}

      {/* AGC: Alerts List */}
      {allItems.length > 0 && (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {allItems.map((item, index) => (
            <AlertItem
              key={item.isEvent ? (item.item as StressEvent).id : `alert-${index}`}
              alert={item.item}
              isEvent={item.isEvent}
              onStatusChange={onEventStatusChange}
              onViewOnMap={item.isEvent && onViewOnMap ? () => onViewOnMap(item.item as StressEvent) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default StressAlertsPanel

